/**
 * Map a socket-like event emitted by a preview/live iframe to the event name
 * that tgb-sockets would relay, so the Producer can dispatch the matching
 * `socket_*` store action.
 *
 * Newer clients emit `updateProduction`/`updateSettings` for settings changes
 * (including live `index`/`sub_index`); the socket server relays those to other
 * clients as `timelineSettings`, so the message-transport bridge must map them
 * the same way.
 *
 * @param {string} action - Event name emitted by the iframe.
 * @returns {string} Event name to dispatch on the Producer store.
 */
export function mapIframeEventToAction(action) {
  const eventMap = {
    updateTimeline: 'timeline',
    settings: 'timelineSettings',
    updateProduction: 'timelineSettings',
    updateSettings: 'timelineSettings',
    playDuration: 'playbackDuration',
    stopVideo: 'stopPlayback',
  };

  return eventMap[action] || action;
}

/**
 * Send a socket-like event to preview/live iframes in the Producer UI
 * via postMessage. This allows tgb-live iframes using transport=message
 * to receive the same data they would get via the socket server.
 *
 * By default sends to both live and preview iframes.
 * Pass `{ live: true }` or `{ preview: true }` as the last argument to target
 * only specific iframe types.
 *
 * @param {string} action - The socket event name (e.g. 'timeline', 'settings', 'onReceiveAction')
 * @param {...any} args - Arguments to pass to the socket handler. If the last argument
 *   is an object with `$frameTarget` set, it's used to filter which iframes receive the message.
 */
export default function sendToPreviewIframes(action, ...args) {
  const payload = args.slice();

  // Check if last arg is frame target filter
  let targetLive = true;
  let targetPreview = true;

  const lastArg = payload[payload.length - 1];
  if (lastArg && lastArg.$frameTarget) {
    targetLive = !!lastArg.live;
    targetPreview = !!lastArg.preview;
    payload.pop();
  }

  const selectors = [];

  if (targetLive) {
    selectors.push('[data-test="producer-live-iframe"]');
  }

  if (targetPreview) {
    selectors.push('[data-test="producer-preview-iframe"]');
  }

  if (!selectors.length) { return; }

  const iframes = document.querySelectorAll(selectors.join(','));

  if (!iframes.length) { return; }

  // Deep clone args to strip Vuex reactive proxies so postMessage can serialize them
  const message = { action, args: JSON.parse(JSON.stringify(payload)) };

  iframes.forEach((iframe) => {
    if (iframe.contentWindow) {
      // Derive target origin from the iframe's src to avoid using the wildcard
      const targetOrigin = (() => {
        try {
          const url = new URL(iframe.src, window.location.origin);
          return `${url.protocol}//${url.host}`;
        } catch (e) {
          return '*';
        }
      })();

      iframe.contentWindow.postMessage(message, targetOrigin);
    }
  });
}
